import {
    type FC,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    ActionButton,
    CancelButton,
    DialogButtons,
    DialogContent,
    Icon,
    Input,
    LayerContentContext,
    RetractableLabel,
    StatusInput,
    type StatusInputRef,
    ToastTypeEnum,
    useToastCreate,
} from "@onshopfront/components";
import Request from "../../../Utilities/Platform/Request";

interface Props {
    id: string;
    requireUpdate?: boolean;
}

export const UpdatePasswordDialog: FC<Props> = ({ id, requireUpdate }) => {
    const statusRef = useRef<StatusInputRef>(null);

    const { id: layerId, remove } = useContext(LayerContentContext);

    const [ errorOverride, setErrorOverride ] = useState(false);
    const [ updating, setUpdating ] = useState(false);
    const [ password, setPassword ] = useState("");
    const [ confirmPassword, setConfirmPassword ] = useState("");

    const createToast = useToastCreate();

    useEffect(() => {
        statusRef.current?.runStatusCheck();
    }, [ password ]);

    const onUpdate = async () => {
        if(password !== confirmPassword) {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Ensure that the passwords match",
            });

            return;
        }

        setUpdating(true);

        try {
            const response = await Request.post<{
                errors?: Array<unknown>;
                data: {
                    updateUser: {
                        id: string;
                    };
                };
            }>("/graphql", {
                query: `
                    mutation UpdateUser(
                        $id: ID!,
                        $password: String,
                    ) {
                        updateUser(
                            id: $id,  
                            password: $password,
                        ) {
                            id
                        }
                    }
                `,
                variables: {
                    id,
                    password,
                },
            });

            if(response.data.errors) {
                throw response.data.errors;
            }

            remove(layerId);

            createToast({
                type   : ToastTypeEnum.Success,
                message: "Successfully updated the user password",
            });
        } catch(err) {
            setUpdating(false);
            setErrorOverride(true);

            console.error("Error updating password", err);
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Unable to update password",
            });
        }
    };

    return (
        <>
            <DialogContent className="max-w-lg w-[80vw] space-y-2">
                <div>Enter the new password</div>
                <RetractableLabel
                    label="Password"
                    retracted={!!password}
                >
                    {(onFocus, onBlur) => (
                        <Input
                            type="password"
                            value={password}
                            onChange={e => {
                                setPassword(e.currentTarget.value);
                            }}
                            data-testid="update-password-password"
                            onFocus={onFocus}
                            onBlur={onBlur}
                        />
                    )}
                </RetractableLabel>
                <RetractableLabel
                    label="Confirm Password"
                    retracted={!!confirmPassword}
                >
                    {(onFocus, onBlur) => (
                        <StatusInput
                            ref={statusRef}
                            type="password"
                            value={confirmPassword}
                            onChange={e => {
                                setConfirmPassword(e.currentTarget.value);
                            }}
                            checkStatus={value => {
                                if(!value) {
                                    return null;
                                }

                                if(value === password) {
                                    return true;
                                }

                                return [
                                    false,
                                    "Passwords don't match",
                                ] as const;
                            }}
                            data-testid="update-password-confirm"
                            onFocus={onFocus}
                            onBlur={onBlur}
                        />
                    )}
                </RetractableLabel>
            </DialogContent>
            <DialogButtons>
                {(!requireUpdate || errorOverride) && (
                    <CancelButton
                        disabled={updating}
                        onClick={() => {
                            remove(layerId);
                        }}
                        data-testid="update-password-cancel"
                    />
                )}
                <ActionButton
                    disabled={password !== confirmPassword || updating || !password.trim()}
                    onClick={onUpdate}
                    data-testid="update-password-update"
                >
                    {updating ? (
                        <Icon
                            icon={[ "fal", "spinner-third" ]}
                            spin
                        />
                    ) : (
                        "Update"
                    )}
                </ActionButton>
            </DialogButtons>
        </>
    );
};
